import { useEffect, useRef, useState } from 'react';
import { usePipelineRun } from '../hooks/use-pipelines';
import { PipelineLog } from '../types/pipeline';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RefreshCw, Search, X } from 'lucide-react';
import { format } from 'date-fns';

interface ExecutionLogsViewerProps {
  pipelineId: string;
  runId: string;
  autoScroll?: boolean;
}

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'ALL';

export function ExecutionLogsViewer({
  pipelineId,
  runId,
  autoScroll = false,
}: ExecutionLogsViewerProps) {
  const [filterLevel, setFilterLevel] = useState<LogLevel>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [groupByNode, setGroupByNode] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error, refetch } = usePipelineRun(pipelineId, runId);

  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [data?.logs, autoScroll]);

  const getLogLevelColor = (level: string) => {
    switch (level) {
      case 'ERROR':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'WARN':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'INFO':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'DEBUG':
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const filterLogs = (logs: PipelineLog[]) => {
    return logs.filter((log) => {
      // Filter by level
      if (filterLevel !== 'ALL' && log.level !== filterLevel) {
        return false;
      }

      // Filter by search query
      if (searchQuery && !log.message.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      return true;
    });
  };

  const groupLogsByNode = (logs: PipelineLog[]) => {
    const grouped = new Map<string, PipelineLog[]>();
    logs.forEach((log) => {
      const nodeId = log.nodeId || 'system';
      if (!grouped.has(nodeId)) {
        grouped.set(nodeId, []);
      }
      grouped.get(nodeId)!.push(log);
    });
    return grouped;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading execution logs...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
        <p className="text-sm text-destructive">
          Failed to load execution logs. Please try again.
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center">
        <p className="text-sm text-muted-foreground">No run data available.</p>
      </div>
    );
  }

  const filteredLogs = filterLogs(data.logs || []);
  const groupedLogs = groupByNode ? groupLogsByNode(filteredLogs) : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Execution Logs</h3>
          <p className="text-sm text-muted-foreground">
            Run ID: {runId} • Status: <Badge variant="outline">{data.status}</Badge>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Run Summary */}
      <div className="grid grid-cols-4 gap-4 rounded-lg border p-4">
        <div>
          <p className="text-xs text-muted-foreground">Started</p>
          <p className="text-sm font-medium">
            {format(new Date(data.startedAt), 'MMM dd, HH:mm:ss')}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Duration</p>
          <p className="text-sm font-medium">
            {data.duration ? `${(data.duration / 1000).toFixed(2)}s` : '-'}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Records Processed</p>
          <p className="text-sm font-medium">{data.recordsProcessed?.toLocaleString() || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Success / Failed</p>
          <p className="text-sm font-medium">
            <span className="text-green-600">{data.recordsSuccess || 0}</span> /{' '}
            <span className="text-red-600">{data.recordsFailed || 0}</span>
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <Select value={filterLevel} onValueChange={(value) => setFilterLevel(value as LogLevel)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Log Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Levels</SelectItem>
            <SelectItem value="DEBUG">Debug</SelectItem>
            <SelectItem value="INFO">Info</SelectItem>
            <SelectItem value="WARN">Warning</SelectItem>
            <SelectItem value="ERROR">Error</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant={groupByNode ? 'default' : 'outline'}
          size="sm"
          onClick={() => setGroupByNode(!groupByNode)}
        >
          Group by Node
        </Button>
      </div>

      {/* Error Message */}
      {data.errorMessage && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm font-semibold text-destructive">Error Message:</p>
          <p className="text-sm text-destructive/90 mt-1">{data.errorMessage}</p>
        </div>
      )}

      {/* Logs Display */}
      <div className="rounded-lg border">
        <div className="max-h-[600px] overflow-y-auto p-4 space-y-2 font-mono text-xs">
          {filteredLogs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No logs match the current filters.
            </p>
          ) : groupedLogs ? (
            // Grouped by node
            Array.from(groupedLogs.entries()).map(([nodeId, logs]) => (
              <div key={nodeId} className="space-y-2">
                <div className="sticky top-0 bg-background border-b pb-2 mb-2">
                  <Badge variant="outline">Node: {nodeId}</Badge>
                </div>
                {logs.map((log, index) => (
                  <div key={`${nodeId}-${index}`} className="flex items-start gap-2 hover:bg-muted/50 p-2 rounded">
                    <span className="text-muted-foreground whitespace-nowrap">
                      {format(new Date(log.timestamp), 'HH:mm:ss.SSS')}
                    </span>
                    <Badge variant="outline" className={`${getLogLevelColor(log.level)} shrink-0`}>
                      {log.level}
                    </Badge>
                    <span className="flex-1 break-all">{log.message}</span>
                  </div>
                ))}
              </div>
            ))
          ) : (
            // Flat list
            filteredLogs.map((log, index) => (
              <div key={index} className="flex items-start gap-2 hover:bg-muted/50 p-2 rounded">
                <span className="text-muted-foreground whitespace-nowrap">
                  {format(new Date(log.timestamp), 'HH:mm:ss.SSS')}
                </span>
                <Badge variant="outline" className={`${getLogLevelColor(log.level)} shrink-0`}>
                  {log.level}
                </Badge>
                {log.nodeId && (
                  <Badge variant="secondary" className="shrink-0">
                    {log.nodeId}
                  </Badge>
                )}
                <span className="flex-1 break-all">{log.message}</span>
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      </div>

      {/* Logs Count */}
      <p className="text-xs text-muted-foreground text-center">
        Showing {filteredLogs.length} of {data.logs?.length || 0} logs
      </p>
    </div>
  );
}
